import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, ReceiptLong as LedgerIcon, Store as StoreIcon,
} from '@mui/icons-material';
import { accountingAPI } from '../../services/api';
import { money, errorText } from '../../utils/format';
import { fmtDate } from '../../utils/dates';

const CATEGORIES = ['SUPPLIER', 'CONTRACTOR', 'SERVICE_PROVIDER', 'HOSPITAL', 'OTHER'];
const emptyForm = () => ({ name: '', category: 'SUPPLIER', contactPerson: '', phone: '', email: '', gstin: '', address: '', notes: '', active: true });

export default function VendorsTab({ notify }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [confirm, setConfirm] = useState(null);
  const [ledger, setLedger] = useState(null);

  const load = useCallback(async () => {
    try { setAll((await accountingAPI.getVendors(true)).data); }
    catch (e) { notify(errorText(e, 'Could not load vendors'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const rows = all.filter((v) => showInactive || v.active)
    .filter((v) => !q || `${v.name} ${v.category || ''} ${v.contactPerson || ''}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const openDialog = (v = null) => {
    setEditing(v);
    setForm(v ? { ...emptyForm(), ...v } : emptyForm());
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { notify('Please enter the vendor name', 'error'); return; }
    try {
      if (editing) await accountingAPI.updateVendor(editing.id, form); else await accountingAPI.createVendor(form);
      notify(editing ? 'Vendor updated' : 'Vendor added');
      setOpen(false);
      load();
    } catch (e) { notify(errorText(e, 'Could not save the vendor'), 'error'); }
  };

  const remove = async () => {
    try {
      const res = await accountingAPI.deleteVendor(confirm.id);
      notify(res.data?.message || 'Vendor deleted');
      setConfirm(null);
      load();
    } catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  const openLedger = async (v) => {
    try { setLedger((await accountingAPI.getVendorLedger(v.id)).data); }
    catch (e) { notify(errorText(e, 'Could not load the vendor ledger'), 'error'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search vendors..." value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <FormControlLabel control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />} label="Show inactive" />
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Add vendor</Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Name</TableCell><TableCell>Category</TableCell><TableCell>Contact</TableCell>
              <TableCell align="right">Total paid</TableCell><TableCell align="right">Transactions</TableCell>
              <TableCell>Last payment</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map((v) => (
                <TableRow key={v.id} hover sx={{ opacity: v.active ? 1 : 0.55 }}>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><StoreIcon fontSize="small" sx={{ color: '#6a1b9a' }} />{v.name}{!v.active && <Chip size="small" label="Inactive" />}</TableCell>
                  <TableCell><Chip size="small" label={v.category || 'OTHER'} /></TableCell>
                  <TableCell>{v.contactPerson || '-'}{v.phone ? ` · ${v.phone}` : ''}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{money(v.totalPaid)}</TableCell>
                  <TableCell align="right">{v.transactionCount ?? 0}</TableCell>
                  <TableCell>{v.lastPaymentDate ? fmtDate(v.lastPaymentDate) : '-'}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Ledger"><IconButton size="small" onClick={() => openLedger(v)}><LedgerIcon fontSize="small" /></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => openDialog(v)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setConfirm(v)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>No vendors yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{editing ? 'Edit vendor' : 'Add vendor'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={7}><TextField fullWidth size="small" label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={5}>
              <TextField select fullWidth size="small" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Contact person" value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="GSTIN" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" multiline rows={2} label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Grid>
            {editing && (
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label="Active" />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete vendor?</DialogTitle>
        <DialogContent><Typography>{confirm?.name} will be removed. If it has payment history it will be deactivated instead.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>

      <Dialog open={!!ledger} onClose={() => setLedger(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{ledger?.vendor?.name} — Ledger</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Title</TableCell><TableCell>Bill no</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
            <TableBody>
              {(ledger?.transactions || []).map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{fmtDate(t.date)}</TableCell>
                  <TableCell>{t.title}</TableCell>
                  <TableCell>{t.billNo || '-'}</TableCell>
                  <TableCell align="right">{money(t.amount)}</TableCell>
                </TableRow>
              ))}
              {(!ledger?.transactions || ledger.transactions.length === 0) && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No transactions yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setLedger(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
